import clsx from 'clsx';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spinner, SpinnerPositioning, SpinnerSize } from '@vestico/shared';

import { TranslationsContext } from '../TranslationsContext';
import { ReviewContentItem } from './ReviewContentItem';
import style from './ReviewContentItemContentReviewText.module.css';

export interface ReviewContentItemContentReviewTextProps {
  reviewText?: string;
  reviewTextLanguage?: string;
  postDocumentID: string;
}

const REVIEW_TEXT_LANGUAGE_EMOJI_ONLY = 'und';

export const ReviewContentItemContentReviewText = ({
  postDocumentID: postDocID,
  reviewText,
  reviewTextLanguage,
}: ReviewContentItemContentReviewTextProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const [loadingTranslate, setLoadingTranslate] = useState(false);
  const [translatedText, setTranslatedText] = useState<string | undefined | null>(undefined);

  const showTranslateButton = useMemo(() => {
    const hasReviewText = !!reviewText && reviewText.length > 0;
    const hasReviewTextLanguage = !!reviewTextLanguage && reviewTextLanguage.length > 0 && reviewTextLanguage !== REVIEW_TEXT_LANGUAGE_EMOJI_ONLY;
    // The language code might be localized to the country. We can't run translation on that so we compare the first two letters only
    // E.g. en-US vs en-GB should not be translated
    const isSameLanguage = !!reviewTextLanguage && !!language && reviewTextLanguage.startsWith(language.substring(0, 2));
    const didQueryTranslationButNoResult = translatedText === null;
    return !didQueryTranslationButNoResult && hasReviewText && hasReviewTextLanguage && !isSameLanguage;
  }, [reviewText, reviewTextLanguage, language, translatedText]);

  const { getPostReviewTranslation } = useContext(TranslationsContext);

  const translateReviewText = useCallback(async () => {
    if (!showTranslateButton) {
      return;
    }

    setLoadingTranslate(true);
    const translationText = await getPostReviewTranslation(postDocID);
    setTranslatedText(translationText);
    setLoadingTranslate(false);
  }, [showTranslateButton, getPostReviewTranslation, postDocID]);

  const showOriginalReviewText = useCallback(() => {
    setTranslatedText(undefined);
  }, []);

  if (!reviewText || reviewText.length === 0) {
    return null;
  }

  const translateButtonText = !translatedText ? t('LOC_FIT_REVIEW_TRANSLATE_BUTTON') : t('LOC_FIT_REVIEW_TRANSLATE_SHOW_ORIGINAL_BUTTON');
  const translateButtonAction = !translatedText ? translateReviewText : showOriginalReviewText;

  return (
    <ReviewContentItem
      title={t('LOC_REVIEW_CONTENT_TITLE_REVIEW_TEXT')}
      content={
        <>
          <span>{translatedText ?? reviewText}</span>
          {showTranslateButton ? (
            <div className={clsx(style.reviewButtonContainer)}>
              {loadingTranslate ? (
                <div className={clsx(style.spinnerContainer)}>
                  <Spinner size={SpinnerSize.mini} positioning={SpinnerPositioning.default} />
                </div>
              ) : null}
              <Button size="small" plain underline disabled={loadingTranslate} onClick={translateButtonAction} content={translateButtonText} />
            </div>
          ) : null}
        </>
      }
    />
  );
};
